<template>
    <new-student-backend-layout
        page-title="Live Webinars"
        content-bg-color="gray-125"
        header-bg-color="white"
        super-banner-color="primary-150"
    >
        <template #super_banner>
            <LiveEventsNextLiveBanner :highlight-webinar="highlightWebinar" />
        </template>

        <template #page_header>
            <div class="flex flex-col gap-1">
                <h1 class="text-[26px] font-bold">Live Events</h1>
                <div class="text-[16px] text-gray-700">
                    Live webinars with our certified EMS instructors
                </div>
            </div>
            <div
                v-if="
                    highlightWebinar &&
                    (highlightWebinar.status === 'preparing' ||
                        highlightWebinar.status === 'started')
                "
                class="mt-4 flex w-full flex-col gap-2"
            >
                <div class="text-[18px] text-gray-700">
                    <span class="font-semibold text-gray-700">Live Now:</span>
                    {{ highlightWebinar.title }}
                </div>
                <div class="w-full">
                    <div class="grid min-h-[400px] w-full grid-cols-5 gap-0">
                        <div
                            class="col-span-5 flex h-full flex-col justify-center border border-gray-225 bg-black lg:col-span-3"
                        >
                            <div
                                class="aspect-video w-full"
                                v-html="highlightWebinar.video_embed_code"
                            ></div>
                        </div>
                        <div
                            class="col-span-5 min-h-[400px] min-w-[200px] border border-gray-225 lg:col-span-2"
                        >
                            <div
                                class="h-full"
                                v-html="highlightWebinar.chat_embed_code"
                            ></div>
                        </div>
                    </div>
                </div>

                <div class="flex gap-4">
                    <div class="mb-4 flex items-center">
                        <div class="flex flex-grow flex-col">
                            <div class="mt-4 text-[14px] text-neutral-60">
                                <p>
                                    <strong>Start Time:</strong>
                                    {{
                                        dayjs(highlightWebinar.starts_at)
                                            .utc(false)
                                            .tz($page.props.display_timezone)
                                            .format(
                                                $page.props.date_formats
                                                    .full_nice
                                            )
                                    }}
                                </p>
                                <p>
                                    <strong>Host:</strong>
                                    {{ highlightWebinar.instructor }}
                                </p>
                                <p>
                                    <strong>Format:</strong>
                                    {{ highlightWebinar.format }}
                                </p>
                                <p>
                                    <strong>Topics: </strong
                                    >{{ highlightWebinar.topics_covered }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #content>
            <div
                class="flex items-center justify-start px-4 pb-4 pt-8 lg:px-10"
            >
                <Link
                    :href="route('student.live-events.index')"
                    class="border border-gray-800 bg-gray-800 px-3 py-2 text-[16px] text-white hover:cursor-pointer"
                >
                    Webinar Schedule
                </Link>
                <Link
                    :href="route('student.live-events.past-recordings')"
                    class="border border-gray-800 px-3 py-2 text-[16px] text-gray-800 hover:cursor-pointer"
                >
                    Past Recordings
                </Link>
            </div>

            <div class="px-4 py-4 lg:px-10">
                <div class="hidden w-full bg-white p-4 shadow md:block">
                    <div class="mb-8 text-[18px] font-semibold text-gray-925">
                        Webinar Schedule
                    </div>
                    <table class="w-full">
                        <thead
                            class="border-b border-gray-300 text-left text-[14px] font-semibold"
                        >
                            <th class="px-4 py-2 pl-0">Webinar</th>
                            <th class="px-4 py-2">Date</th>
                            <th class="px-4 py-2">Time</th>
                            <th class="px-4 py-2">Levels</th>
                            <th class="px-4 py-2 pr-0"></th>
                        </thead>
                        <tbody>
                            <template v-for="webinar in upcomingWebinars">
                                <tr
                                    class="border-b border-gray-300 text-left last-of-type:border-b-0"
                                >
                                    <td class="px-4 py-2 pl-0">
                                        <div
                                            class="text-[16px] font-semibold text-gray-700"
                                        >
                                            {{ webinar.title }}
                                        </div>
                                        <div class="text-[16px] text-gray-600">
                                            {{ webinar.topics_covered }}
                                        </div>
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-4 py-2 text-[16px] text-gray-600"
                                    >
                                        {{
                                            dayjs(webinar.starts_at)
                                                .utc(false)
                                                .tz(
                                                    $page.props.auth.user
                                                        .timezone ??
                                                        "America/Los_Angeles"
                                                )
                                                .format("ddd MMMM Do")
                                        }}
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-4 py-2 text-[16px] text-gray-600"
                                    >
                                        {{
                                            dayjs(webinar.starts_at)
                                                .utc(false)
                                                .tz(
                                                    $page.props.auth.user
                                                        .timezone ??
                                                        "America/Los_Angeles"
                                                )
                                                .format("h:mmA z")
                                        }}
                                    </td>
                                    <td
                                        class="whitespace-nowrap px-4 py-2 text-[16px] text-gray-600"
                                    >
                                        <span
                                            v-if="
                                                webinar.provider_levels
                                                    .length >=
                                                $page.props.providerLevels
                                                    .length
                                            "
                                            >All Levels</span
                                        >
                                        <span
                                            v-else
                                            class="after:content-['•_'] last-of-type:after:content-['']"
                                            v-for="providerLevel in webinar.provider_levels"
                                            >{{ providerLevel.title }}</span
                                        >
                                    </td>
                                    <td
                                        class="relative whitespace-nowrap px-4 py-2 pr-0 text-right"
                                    >
                                        <AddToCalendarWidget :event="webinar" />
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
                <div class="md:hidden">
                    <template v-for="webinar in upcomingWebinars">
                        <div class="mb-4 bg-white p-4 shadow">
                            <div
                                class="mb-2 text-[16px] font-semibold text-gray-700"
                            >
                                {{ webinar.title }}
                            </div>
                            <div class="mb-2 text-[16px] text-gray-600">
                                {{
                                    dayjs(webinar.starts_at)
                                        .utc(false)
                                        .tz(
                                            $page.props.auth.user.timezone ??
                                                "America/Los_Angeles"
                                        )
                                        .format("ddd MMMM Do • HH:mm A z")
                                }}
                            </div>
                            <div class="mb-4 text-[16px] text-gray-700">
                                {{ webinar.topics_covered }}
                            </div>
                            <div class="mb-8 flex flex-wrap gap-2">
                                <div
                                    class="rounded-full bg-gray-300 px-4 py-2 text-[16px] font-semibold text-gray-700"
                                    v-if="
                                        webinar.provider_levels.length >=
                                        $page.props.providerLevels.length
                                    "
                                >
                                    All Levels
                                </div>
                                <div
                                    v-else
                                    class="rounded-full bg-gray-300 px-4 py-2 text-[16px] font-semibold text-gray-700"
                                    v-for="providerLevel in webinar.provider_levels"
                                >
                                    {{ providerLevel.title }}
                                </div>
                            </div>
                            <div class="">
                                <AddToCalendarWidget
                                    :event="webinar"
                                    button-css="block w-full rounded bg-primary-525 px-3 py-2 text-center text-[16px] text-white hover:cursor-pointer hover:bg-primary-600"
                                />
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </new-student-backend-layout>
</template>

<script>
import NewStudentBackendLayout from "@/_Layouts/NewStudentBackendLayout.vue";
import AddToCalendarWidget from "@/Components/AddToCalendarWidget.vue";
import StudentVideoCard from "@/Components/StudentVideoCard.vue";
import LiveEventsNextLiveBanner from "@/Components/LiveEventsNextLiveBanner.vue";

export default {
    name: "StudentLiveEventsIndex",
    components: {
        LiveEventsNextLiveBanner,
        StudentVideoCard,
        AddToCalendarWidget,
        NewStudentBackendLayout,
    },
    props: {
        upcomingWebinars: {
            type: Object,
            default: {},
        },
        highlightWebinar: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {};
    },
    methods: {},
};
</script>
